import { FirebaseEvents } from "../../../utils/firebaseEvents";
import { ActionValidator } from "../../../auth/actionValidator";
import { DataCreator } from "../../../utils/dataCreator";

import { Utilities } from "../../../utils/utilities";
import { ToastUtilities } from "../../../libs/react-toastify/ToastUtilities";
import { FirebaseData } from "../General/FirebaseData";
import { LocalData } from "../General/LocalData";
import { PropertyData } from "../Property/PropertyData";
import { UserData } from "../User/UserData";
import { DeviationData } from "./DeviationData";
import { AuthenticationTokenData } from "../General/AuthenticationTokenData";
import { RelativeData } from "../General/RelativeData";
import { t } from "i18next";
import { FirebaseFile } from "../General/FirebaseFile";
import { SubtaskStatus } from "./Subtask/SubtaskStatus";
import { SubtaskFieldData } from "./Subtask/SubtaskFieldData";
import { deleteField } from "firebase/firestore";
import { useChecklistAccess } from "@src/auth/featureAccess/useChecklistAccess";

export class SubtaskData extends FirebaseData {
  title: string = "";
  description: string = "";
  deviation: DeviationData;
  user?: UserData;
  createDate?: Date;
  status?: SubtaskStatus;
  responsible: UserData[] = [];
  fields: SubtaskFieldData[] = [];
  fieldsOrder: string[] = [];
  routineTaskID: string = "";
  relatives: RelativeData[] = [];
  files: FirebaseFile[] = [];

  constructor(id: string, collection: string, parent: FirebaseData | undefined) {
    super(id, collection, parent);
    this.deviation = parent as DeviationData;
  }

  setup(data: any) {
    this.title = typeof data.title === "string" ? data.title : "";
    this.description = typeof data.description === "string" ? data.description : "";
    this.user = typeof data.creatorID === "string" ? LocalData.users.addData(data.creatorID) : undefined;
    this.responsible = typeof data.responsible === "object" ? Object.keys(data.responsible).map((userId) => LocalData.users.addData(userId)) : [];
    this.routineTaskID = typeof data.routineTaskID === "string" ? data.routineTaskID : "";
    this.createDate = Utilities.getDateFromTimestamp(data.createDate);

    // Status
    this.status = undefined;
    if (typeof data.status === "object" && typeof data.status.value === "number") {
      this.status = SubtaskStatus.getStatusWithId(data.status.value);
      if (typeof data.status.changedBy === "string") {
        this.status.changedBy = LocalData.users.addData(data.status.changedBy);
      }
      this.status.changedDate = Utilities.getDateFromTimestamp(data.status.changedDate);
    }

    // Fields
    this.fields = [];
    this.fieldsOrder = Array.isArray(data.fieldsOrder) ? data.fieldsOrder : [];

    if (typeof data.fields === "object") {
      for (const fieldID of Object.keys(data.fields)) {
        const field = data.fields[fieldID];
        const type = field.valueType;
        if (typeof type === "string" && ["number", "checkbox", "string", "selection"].includes(type)) {
          this.fields.push({
            id: fieldID,
            title: typeof field.title === "string" ? field.title : "",
            value: field.value,
            valueType: type as SubtaskFieldData["valueType"],
            unit: typeof field.unit === "string" ? field.unit : "",
            options: Array.isArray(field.options) ? field.options : [],
          });
        }
      }
    }

    // Relatives
    this.relatives = [];
    if (typeof data.relatives === "object") {
      for (const relativeID of Object.keys(data.relatives)) {
        this.relatives.push(new RelativeData(relativeID, data.relatives[relativeID]));
      }
    }

    //Files
    this.files = [];
    if (typeof data.files === "object") {
      for (const fileId in data.files) {
        const file = FirebaseFile.initialize(fileId, data.files[fileId]);
        this.files.push(file);
      }
    }

    super.setup(data);
  }

  toggleStatus() {
    const newStatus = this.status?.id === 1 ? 0 : 1;
    ToastUtilities.presentSavingToast(
      super
        .saveUpdate({
          status: {
            value: newStatus,
            changedBy: LocalData.currentUser.id,
            changedDate: new Date(),
          },
          updatedBy: DataCreator.getUpdatedBy(),
        })
        .then(() => {
          if (newStatus === 1 && this.createDate) {
            FirebaseEvents.logEvent("deviation_task_completed", {
              minutes_since_created: Utilities.secondsBetweenDates(this.createDate, new Date()) / 60,
            });
          }
        })
    );
  }

  toggleResponsible(responsible: boolean, user: UserData, completion: (error?: Error) => void) {
    let responsibleUsers: { [key: string]: any } = {};
    this.responsible.forEach((responsibleUser) => {
      if (responsibleUser !== user || (responsible === true && responsibleUser === user)) {
        responsibleUsers[responsibleUser.id] = {};
      }
    });
    if (!responsibleUsers[user.id] && responsible) {
      responsibleUsers[user.id] = {};
    }

    if (this.deviation.parent instanceof PropertyData) {
      this.saveUpdateWithCompletionHandler(
        {
          responsible: responsibleUsers,
          updatedBy: DataCreator.getUpdatedBy(),
        },
        (error) => {
          if (error) {
            completion(error);
          } else {
            this.responsible = [];
            for (let userId in responsibleUsers) {
              let user = LocalData.users.addData(userId);
              this.responsible.push(user);
            }
            completion(undefined);
          }
        }
      );
    }
  }

  isAdministrator(user: UserData): boolean {
    return (
      this.responsible.includes(user) || this.user === user || this.deviation.responsibles.includes(user) || this.deviation.executors.includes(user)
    );
  }

  /**
   * @deprecated Use {@link useChecklistAccess} hook instead
   */
  hasExternalAccess(): boolean {
    const externalDeviation = AuthenticationTokenData.getExternalDeviationDataWithId(this.deviation.id);
    if (LocalData.currentUser.isExternal && externalDeviation) {
      return externalDeviation.tasks === "all" || externalDeviation.tasks.includes(this);
    }
    return false;
  }
  isChecked(): boolean {
    return this.status === SubtaskStatus.DONE;
  }

  /**
   * @deprecated Use {@link useChecklistAccess} hook instead
   */
  canAdministrate(): boolean {
    return (
      (this.deviation.parent instanceof PropertyData &&
        ActionValidator.isAllowedToPerformAction("subtask_administrate_settings", [this.deviation.parent])) ||
      this.isAdministrator(LocalData.currentUser) ||
      this.hasExternalAccess()
    );
  }

  /**
   * @deprecated Use {@link useChecklistAccess} hook instead
   */
  canAdministrateResponsible(): boolean {
    return (
      (this.deviation.parent instanceof PropertyData &&
        ActionValidator.isAllowedToPerformAction("subtask_administrate_responsible", [this.deviation.parent])) ||
      this.isAdministrator(LocalData.currentUser) ||
      this.hasExternalAccess()
    );
  }

  getCheckError(): string | undefined {
    if (!this.deviation.settings.checklistFieldsRequired) {
      return undefined;
    }
    const missingFields = this.fields.filter((field) => field.value === undefined || field.value === null || field.value === "");
    if (missingFields.length > 0) {
      return t("main.taskSettings.checklistFieldsRequired.error");
    }
    return undefined;
  }

  /**
   * @deprecated Use {@link useChecklistAccess} hook instead
   */
  canCheck(): boolean {
    return (
      !this.getCheckError() &&
      ((this.deviation.parent instanceof PropertyData &&
        ActionValidator.isAllowedToPerformAction("subtask_mark_completed", [this.deviation.parent])) ||
        this.isAdministrator(LocalData.currentUser) ||
        this.hasExternalAccess())
    );
  }

  uploadFile(file: FirebaseFile): Promise<FirebaseFile> {
    const saveToFirestore = () =>
      this.saveUpdate({
        ["files." + file.id]: file.getFirebaseMap(),
      }).then(() => {
        this.files.push(file);
        return file;
      });

    if (file.path) {
      return new Promise<FirebaseFile>((resolve, reject) => {
        file.saveAndLoadURL((success) => {
          if (success) {
            saveToFirestore()
              .then(() => {
                resolve(file);
              })
              .catch((error) => reject(error));
          } else {
            reject();
          }
        });
      });
    } else {
      return saveToFirestore();
    }
  }
  deleteFile(file: FirebaseFile): Promise<any> {
    const deleteFromFirestore = () =>
      this.saveUpdate({
        ["files." + file.id]: deleteField(),
      });

    if (file.path) {
      return new Promise<void>((resolve, reject) => {
        file.deleteFileFromStorage((success) => {
          if (success) {
            deleteFromFirestore()
              .then(() => {
                this.files.splice(this.files.indexOf(file), 1);
                resolve();
              })
              .catch((error) => reject(error));
          } else {
            reject();
          }
        });
      });
    } else {
      return deleteFromFirestore();
    }
  }
}
